import React, { useEffect, useState } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { buildTabChangeMap } from '../../../../../utils/dealerApprovalEnums';
import TabChangeBadge from '../../customerAddressBrandPage/components/tabs/TabChangeBadge';
import AddressBrandBlock from './AddressBrandBlock';

const ROW_STATE_KEY = {
    0: 'dealerRegistration.rowState.live',
    1: 'dealerRegistration.rowState.pendingInsert',
    2: 'dealerRegistration.rowState.pendingUpdate'
};

const rowStateSeverity = (code) => {
    if (code === 1) return 'success';
    if (code === 2) return 'warning';
    return 'success';
};

const blockSummary = (block) => {
    const address = block.addressName || block.address || block.sapAddrId || '';
    const brand = block.selectedBrand?.displayValue || block.selectedBrand?.brandShort || '';
    return [address, brand].filter(Boolean).join(' — ');
};

const blockTabChange = (block) => {
    const map = buildTabChangeMap(block.tabChanges || []);
    const totalCount = Object.values(map).reduce((sum, item) => sum + (item.totalCount || 0), 0);
    return totalCount > 0 ? { totalCount } : null;
};

const AddressBrandBlocks = ({ addresses = [], selectedBlockKey, setSelectedBlockKey, addBlock, removeBlock, setBlockField, onBlockAddressChange, onBlockContractStartDateChange, sapDealerId, submitted, readOnly = false }) => {
    const { th } = useLanguage();
    const selectedIndex = Math.max(
        0,
        addresses.findIndex((b) => b.key === selectedBlockKey)
    );
    const [activeIndex, setActiveIndex] = useState(selectedIndex);

    useEffect(() => {
        setActiveIndex(selectedIndex);
    }, [selectedIndex]);

    const handleTabChange = (e) => {
        const index = e.index;
        setActiveIndex(index);
        if (index == null || index < 0) return;
        const block = addresses[index];
        if (block && block.key !== selectedBlockKey) {
            setSelectedBlockKey(block.key);
        }
    };

    const renderHeader = (block) => {
        const canRemove = !readOnly && block.dealerAddressBrandId == null && addresses.length > 1;
        const stateCode = block.rowState ?? 0;
        return (
            <div className="flex align-items-center justify-content-between gap-2 w-full">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
                        <RadioButton inputId={`active-address-${block.key}`} name="dealer-active-address" value={block.key} checked={selectedBlockKey === block.key} onChange={() => setSelectedBlockKey(block.key)} />
                    </span>
                    <label htmlFor={`active-address-${block.key}`} className="font-bold m-0" onClick={(e) => e.stopPropagation()}>
                        {th('dealerRegistration.selectActiveAddress')}
                    </label>
                    {blockSummary(block) && <span className="text-sm text-600">{blockSummary(block)}</span>}
                    {stateCode > 0 && <Tag value={th(ROW_STATE_KEY[stateCode] || ROW_STATE_KEY[0])} severity={rowStateSeverity(stateCode)} />}
                    <TabChangeBadge tabChange={blockTabChange(block)} />
                </div>
                {canRemove && (
                    <Button
                        type="button"
                        icon="pi pi-trash"
                        text
                        rounded
                        severity="danger"
                        aria-label={th('dealerRegistration.removeAddressBlock')}
                        tooltip={th('dealerRegistration.removeAddressBlock')}
                        onClick={(e) => {
                            e.stopPropagation();
                            removeBlock(block.key);
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <div>
            <div className="flex align-items-center justify-content-between mb-3">
                <span className="font-bold">{th('dealerRegistration.form.addressInfo')}</span>
                {!readOnly && <Button type="button" icon="pi pi-plus" label={th('dealerRegistration.addAddressBlock')} outlined size="small" onClick={addBlock} />}
            </div>
            {submitted && addresses.length === 0 && <small className="p-error">{th('dealerRegistration.blockRequired')}</small>}
            <Accordion activeIndex={activeIndex} onTabChange={handleTabChange}>
                {addresses.map((block) => (
                    <AccordionTab key={block.key} header={renderHeader(block)}>
                        <AddressBrandBlock
                            block={block}
                            sapDealerId={sapDealerId}
                            submitted={submitted}
                            readOnly={readOnly}
                            setBlockField={setBlockField}
                            onAddressChange={onBlockAddressChange(block.key)}
                            onContractStartDateChange={onBlockContractStartDateChange(block.key)}
                        />
                    </AccordionTab>
                ))}
            </Accordion>
        </div>
    );
};

export default AddressBrandBlocks;
